import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ProfileContext } from "../../../context/ProfileProvider";
import AuthService from "../../../api/services/AuthService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { Helmet } from "react-helmet-async";
import { isValidPhoneNumber } from "libphonenumber-js";
import { useGoogleLogin } from "@react-oauth/google";
import AuthPhoneField from "../../../customComponents/AuthPhoneField";
import EmailInputWithDomainSuggest from "../../../customComponents/EmailInputWithDomainSuggest";
import { $ } from "react-jquery-plugin";
import {
  getPasswordRuleMeta,
  getPasswordRuleRowStates,
  validatePasswordAgainstRules,
} from "../../../utils/authPasswordRules";
import "./signup-wizard.css";
import { parseIdentifierCheckResponse } from "./signupUtils";
import { persistDeviceIdFromServer } from "../../../utils/deviceId";
import TermsConditionsModal from "../UserTermsConditions/TermsConditionsModal";
import { usePlatformStatus } from "../../../context/PlatformStatusProvider";
import { isAppleSignInConfigured, signInWithApple } from "../../../utils/appleSignIn";

const TOTAL_STEPS = 4;

const getQueryParam = (name) => {
  try {
    return new URLSearchParams(window.location.search).get(name) || "";
  } catch {
    return "";
  }
};

/** Extract JWT from various backend response shapes (verify-otp, register, etc.) */
const pickAuthTokenFromResult = (result) => {
  if (!result || typeof result !== "object") return null;
  const d = result.data;
  if (typeof d === "string" && d.trim().length > 0) {
    return d.replace(/^Bearer\s+/i, "").trim();
  }
  const candidates = [
    result.token,
    result.accessToken,
    result.access_token,
    d?.token,
    d?.accessToken,
    d?.access_token,
    d?.jwt,
    d?.bearerToken,
    d?.authToken,
    d?.sessionToken,
    d?.id_token,
    d && typeof d === "object" && (d.data?.token || d.data?.accessToken),
    result?.result?.token,
    d?.result?.token,
    d?.user?.token,
    d?.user?.accessToken,
    d?.auth?.token,
    d?.auth?.access_token,
  ];
  for (const raw of candidates) {
    if (raw == null || typeof raw !== "string") continue;
    const t = raw.replace(/^Bearer\s+/i, "").trim();
    if (t) return t;
  }
  if (d && typeof d === "object" && !Array.isArray(d)) {
    for (const v of Object.values(d)) {
      if (typeof v === "string" && v.length > 40 && v.startsWith("eyJ") && v.includes(".")) {
        return v.replace(/^Bearer\s+/i, "").trim();
      }
    }
  }
  return null;
};

/** Persist JWT from registration/OTP responses when backend sends it */
const persistAuthTokenFromSignupResult = (result) => {
  const token = pickAuthTokenFromResult(result);
  const d = result?.data && typeof result.data === "object" ? result.data : {};
  const refresh = d.refresh_token || d.refreshToken || result?.refresh_token || result?.refreshToken || null;
  if (token) {
    localStorage.setItem("token", token);
    window.dispatchEvent(new Event("agce-auth-changed"));
    window.dispatchEvent(new Event("agce-auth-token-changed"));
  }
  if (refresh) {
    localStorage.setItem("refresh_token", String(refresh).trim());
  }
  if (d.device_id) persistDeviceIdFromServer(d.device_id);
  const userId = result?.data?.userId ?? result?.data?.id ?? result?.userId;
  if (userId != null && userId !== "") {
    localStorage.setItem("userId", String(userId));
  }
};

/** Same `email_or_phone` shape as LoginPage uses for AuthService.login */
const getLoginIdentifierForSignup = (accountTab, signId) => {
  if (accountTab === "email") return String(signId || "").trim();
  return String(signId || "").replace(/\D/g, "").replace(/^0+/, "") || "";
};

/**
 * verify-registration-otp often returns success without a JWT. Open a normal session
 * with the password the user just set (same as post-login).
 */
const tryObtainSessionViaLoginAfterOtp = async (accountTab, signId, password) => {
  if (localStorage.getItem("token")) return;
  const loginId = getLoginIdentifierForSignup(accountTab, signId);
  if (!loginId || !password) return;
  const loginRes = await AuthService.login(loginId, password, "");
  if (loginRes?.success) {
    persistAuthTokenFromSignupResult(loginRes);
  }
};

const SignupPage = () => {
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const { handleUserDetails } = useContext(ProfileContext) || {};
  const { getStatus, refetch } = usePlatformStatus();
  const invitationFromUrl = getQueryParam("referral_code") || getQueryParam("referral_code");
  const emailFromUrl = getQueryParam("emailId");

  const [accountTab, setAccountTab] = useState(locationState?.accountTab || "email");
  const [wizardStep, setWizardStep] = useState(locationState?.registrationToken ? 3 : 1);
  const [referralOpen, setReferralOpen] = useState(!!invitationFromUrl);
  const [signId, setSignId] = useState(locationState?.signId || emailFromUrl);
  const [password, setPassword] = useState(locationState?.password || "");
  const [countryCode, setCountryCode] = useState(locationState?.countryCode || "+91");
  const [invitation, setInvitation] = useState(invitationFromUrl);
  const [showPassword, setShowPassword] = useState(false);
  const [registrationToken, setRegistrationToken] = useState(locationState?.registrationToken || "");
  const [serverSignId, setServerSignId] = useState("");
  const [registeredBy, setRegisteredBy] = useState("");
  const [otpTimer, setOtpTimer] = useState(0);
  const [otpResendBusy, setOtpResendBusy] = useState(false);
  /** Step 3: verify token + request OTP (inline button loader, no full-screen overlay) */
  const [verifyingTokenBusy, setVerifyingTokenBusy] = useState(false);
  const [registerSubmitting, setRegisterSubmitting] = useState(false);
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [googleSignupBusy, setGoogleSignupBusy] = useState(false);
  const [attemptLeft, setAttemptLeft] = useState("");
  const [otpSingle, setOtpSingle] = useState("");
  const [signupReachedWelcome, setSignupReachedWelcome] = useState(false);
  const [step1Submitting, setStep1Submitting] = useState(false);
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [agreeDangerBorder, setAgreeDangerBorder] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  /** Step 1 email: red border on empty / invalid format / email API error */
  const [step1EmailDangerBorder, setStep1EmailDangerBorder] = useState(false);
  const [step1PhoneDangerBorder, setStep1PhoneDangerBorder] = useState(false);
  const [referralDangerBorder, setReferralDangerBorder] = useState(false);
  /** Red border on OTP cells only; messages use alertErrorMessage */
  const [otpDangerBorder, setOtpDangerBorder] = useState(false);
  const otpInputRefs = useRef([]);
  /** Last OTP we auto-submitted on signup step 3 — avoids double verify / loops after failure. */
  const signupAutoVerifyLastOtpRef = useRef("");
  const signupAutoVerifyInFlightRef = useRef(false);
  const handleOtpSubmitRef = useRef(async () => { });

  const passwordInputType = showPassword ? "text" : "password";
  const referralLocked = Boolean(invitationFromUrl);

  const usernamePart =
    accountTab === "email" && signId.includes("@")
      ? signId.split("@")[0]
      : String(signId || "").replace(/\D/g, "");

  const signupPasswordRules = useMemo(() => getPasswordRuleMeta(usernamePart), [usernamePart]);
  const passwordRuleRowStates = useMemo(
    () => getPasswordRuleRowStates(password, signupPasswordRules),
    [password, signupPasswordRules]
  );

  const passwordMeetsAllRules = useMemo(() => {
    const p = String(password || "");
    if (!p.trim()) return false;
    return signupPasswordRules.every((r) => r.passes(p));
  }, [password, signupPasswordRules]);

  const otpInputsLocked = verifyingTokenBusy || otpSubmitting;

  useEffect(() => {
    $("body").addClass("signupbg");
    return () => {
      $("body").removeClass("signupbg");
    };
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  /** Steps 2–4: full-page centered form; step 1 keeps split layout with left promo */
  useEffect(() => {
    const cls = "signup-wizard-full-steps";
    if (wizardStep > 1) {
      document.body.classList.add(cls);
    } else {
      document.body.classList.remove(cls);
    }
    return () => {
      document.body.classList.remove(cls);
    };
  }, [wizardStep]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [wizardStep, accountTab]);

  /** Step-wise guards: wrong step → correct previous step */
  useEffect(() => {
    if (wizardStep === 3 && !registrationToken) {
      setWizardStep(2);
    }
  }, [wizardStep, registrationToken]);

  useEffect(() => {
    if (wizardStep === 4 && !signupReachedWelcome) {
      setWizardStep((s) => (s === 4 ? 3 : s));
    }
  }, [wizardStep, signupReachedWelcome]);

  useEffect(() => {
    if (wizardStep !== 3 || !registrationToken) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const result = await AuthService.verifyRegistrationToken(registrationToken);
        if (cancelled) return;
        if (result?.success) {
          if (result?.accountStatus === "Inactive") {
            const sid = result?.data?.signId || "";
            const rb = result?.data?.registeredBy || "";
            setServerSignId(sid);
            setRegisteredBy(rb);
            if (sid) {
              const otpResult = await AuthService.registrationOtp(sid, rb);
              if (cancelled) return;
              if (otpResult?.success) {
                setOtpTimer(60);
                setAttemptLeft(otpResult?.attemptsLeft ?? "");
              } else if (otpResult?.message === "No OTP attempt left. Your account has been suspended.") {
                alertErrorMessage(otpResult?.message);
                navigate(`/account-activate/${registrationToken}`, { replace: true });
              } else {
                alertErrorMessage(otpResult?.message || "Could not send verification code. Please use Send code.");
              }
            }
          } else {
            navigate(`/account-activate/${registrationToken}`, { replace: true });
          }
        } else {
          if (result?.accountStatus === "Blocked") {
            navigate(`/account-activate/${registrationToken}`, { replace: true });
          }
          alertErrorMessage(result?.message || "Could not load verification.");
        }
      } catch (e) {
        if (!cancelled) alertErrorMessage(e?.message || "Verification load failed.");
      } finally {
        if (!cancelled) setVerifyingTokenBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [wizardStep, registrationToken, navigate]);

  useEffect(() => {
    if (otpTimer <= 0) return undefined;
    const t = setInterval(() => setOtpTimer((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [otpTimer]);

  useEffect(() => {
    if (wizardStep !== 3) return undefined;
    const id = window.setTimeout(() => otpInputRefs.current[0]?.focus(), 120);
    return () => window.clearTimeout(id);
  }, [wizardStep]);

  useEffect(() => {
    if (wizardStep !== 3) setOtpDangerBorder(false);
  }, [wizardStep]);

  /** Logged-in user opening /signup at step 1 should leave this page. */
  useEffect(() => {
    if (localStorage.getItem("token") && wizardStep === 1) {
      navigate("/user_profile/dashboard", { replace: true });
    }
  }, [navigate, wizardStep]);

  /** Welcome (step 4): token is already in localStorage — force layout to re-check token so AuthHeader shows. */
  useEffect(() => {
    if (wizardStep === 4 && localStorage.getItem("token")) {
      window.dispatchEvent(new Event("agce-auth-token-changed"));
    }
  }, [wizardStep]);

  const switchAccountTab = (tab) => {
    setAccountTab(tab);
    setWizardStep(1);
    setSignId(tab === "email" ? emailFromUrl : "");
    setPassword("");
    setRegistrationToken("");
    setServerSignId("");
    setRegisteredBy("");
    setShowPassword(false);
    setAttemptLeft("");
    setOtpTimer(0);
    setOtpSingle("");
    setOtpDangerBorder(false);
    setStep1EmailDangerBorder(false);
    setStep1PhoneDangerBorder(false);
    setReferralOpen(!!invitationFromUrl);
    setSignupReachedWelcome(false);
    setStep1Submitting(false);
    setVerifyingTokenBusy(false);
    setRegisterSubmitting(false);
    setOtpSubmitting(false);
  };

  const step1Next = async () => {
    if (!getStatus("signup").enabled) {
      alertErrorMessage("Signup is temporarily off");
      return;
    }
    if (!agreeChecked) {
      setAgreeDangerBorder(true);
      alertErrorMessage("Please accept the Terms & Conditions to continue");
      return;
    }
    if (accountTab === "email") {
      const email = signId?.trim() || "";
      if (!email) {
        setStep1EmailDangerBorder(true);
        alertErrorMessage("Please enter your email");
        return;
      }
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(email)) {
        setStep1EmailDangerBorder(true);
        alertErrorMessage("Please enter a valid email address");
        return;
      }
      setStep1EmailDangerBorder(false);
      if (email !== signId) setSignId(email);
      setStep1Submitting(true);
      try {
        const ref = String(invitation || "").trim();
        const emailRes = await AuthService.checkIdentifier({
          identifier: email,
          kind: "email",
          purpose: "signup",
          referralCode: ref,
        });
        const emailCheck = parseIdentifierCheckResponse(emailRes, "signup");
        if (!emailCheck.ok) {
          if (emailCheck.code === "INVALID_REFERRAL_CODE") {
            setReferralDangerBorder(true);
            setReferralOpen(true);
          } else {
            setStep1EmailDangerBorder(true);
          }
          alertErrorMessage(emailCheck.message);
          return;
        }
        setStep1EmailDangerBorder(false);
        setWizardStep(2);
      } catch (e) {
        setStep1EmailDangerBorder(true);
        alertErrorMessage(e?.message || "Could not verify. Please try again.");
      } finally {
        setStep1Submitting(false);
      }
      return;
    }
    const digits = String(signId || "").replace(/\D/g, "").replace(/^0+/, "") || "";
    if (!digits) {
      setStep1PhoneDangerBorder(true);
      alertErrorMessage("Please enter your phone number");
      return;
    }
    const fullPhone = `${countryCode}${digits}`;
    if (!isValidPhoneNumber(fullPhone)) {
      setStep1PhoneDangerBorder(true);
      alertErrorMessage("Please enter a valid phone number for the selected country");
      return;
    }
    if (digits !== signId) setSignId(digits);
    setStep1PhoneDangerBorder(false);
    setStep1Submitting(true);
    try {
      const ref = String(invitation || "").trim();
      const phoneRes = await AuthService.checkIdentifier({
        identifier: digits,
        kind: "phone",
        purpose: "signup",
        countryCode,
        referralCode: ref,
      });
      const phoneCheck = parseIdentifierCheckResponse(phoneRes, "signup");
      if (!phoneCheck.ok) {
        if (phoneCheck.code === "INVALID_REFERRAL_CODE") {
          setReferralDangerBorder(true);
          setReferralOpen(true);
        } else {
          setStep1PhoneDangerBorder(true);
        }
        alertErrorMessage(phoneCheck.message);
        return;
      }
      setWizardStep(2);
    } catch (e) {
      setStep1PhoneDangerBorder(true);
      alertErrorMessage(e?.message || "Could not verify. Please try again.");
    } finally {
      setStep1Submitting(false);
    }
  };

  const validatePasswordStep = () => {
    const res = validatePasswordAgainstRules(password, signupPasswordRules);
    if (!res.ok) {
      alertErrorMessage(res.message);
      return false;
    }
    return true;
  };

  /** Internal step 2: password + register → then internal step 3 = OTP (shown as “step 2” to user). */
  const submitPasswordAndRegister = async () => {
    if (!validatePasswordStep()) return;

    setRegisterSubmitting(true);
    try {
      if (accountTab === "email") {
        const result = await AuthService.registerEmail(signId.trim(), password, invitation, "");
        if (result?.success) {
          alertSuccessMessage(result?.message);
          setRegistrationToken(result?.token || "");
          setOtpSingle("");
          setVerifyingTokenBusy(true);
          setWizardStep(3);
        } else {
          alertErrorMessage(result?.message);
        }
      } else {
        const result = await AuthService.registerPhone(+signId, password, invitation, countryCode, "");
        if (result?.success) {
          alertSuccessMessage(result?.message);
          setRegistrationToken(result?.token || "");
          setOtpSingle("");
          setVerifyingTokenBusy(true);
          setWizardStep(3);
        } else {
          alertErrorMessage(result?.message);
        }
      }
    } catch (error) {
      alertErrorMessage(error?.message || "Registration failed. Please try again.");
    } finally {
      setRegisterSubmitting(false);
    }
  };

  const handleOtpSubmit = async () => {
    const code = otpSingle.replace(/\D/g, "").slice(0, 6);
    if (code.length < 6) {
      setOtpDangerBorder(true);
      alertErrorMessage("Please enter the 6-digit verification code.");
      return;
    }
    if (!serverSignId) {
      setOtpDangerBorder(true);
      alertErrorMessage("Session not ready. Please wait or refresh the page.");
      return;
    }
    setOtpDangerBorder(false);
    setOtpSubmitting(true);
    try {
      const result = await AuthService.verifyRegistrationOtp(serverSignId, +code, registeredBy, "");
      if (result?.success) {
        setOtpDangerBorder(false);
        alertSuccessMessage(result?.message);
        persistAuthTokenFromSignupResult(result);
        await tryObtainSessionViaLoginAfterOtp(accountTab, signId, password);
        if (typeof handleUserDetails === "function" && localStorage.getItem("token")) {
          void handleUserDetails();
        }
        // New account: start KYC VARA onboarding (the user can skip it from there).
        navigate("/kyc-verification", { replace: true });
      } else {
        setOtpDangerBorder(true);
        alertErrorMessage(result?.message || "Verification failed. Please check the code and try again.");
      }
    } catch (error) {
      setOtpDangerBorder(true);
      alertErrorMessage(error?.response?.data?.message || error?.message || "Verification failed.");
    } finally {
      setOtpSubmitting(false);
    }
  };

  handleOtpSubmitRef.current = handleOtpSubmit;

  useEffect(() => {
    if (wizardStep !== 3) {
      signupAutoVerifyLastOtpRef.current = "";
      signupAutoVerifyInFlightRef.current = false;
      return;
    }
    if (!serverSignId || verifyingTokenBusy || otpSubmitting || signupAutoVerifyInFlightRef.current) return;
    const code = otpSingle.replace(/\D/g, "").slice(0, 6);
    if (code.length < 6) {
      signupAutoVerifyLastOtpRef.current = "";
      return;
    }
    if (signupAutoVerifyLastOtpRef.current === code) return;
    signupAutoVerifyLastOtpRef.current = code;
    signupAutoVerifyInFlightRef.current = true;
    void Promise.resolve(handleOtpSubmitRef.current()).finally(() => {
      signupAutoVerifyInFlightRef.current = false;
    });
  }, [otpSingle, wizardStep, serverSignId, verifyingTokenBusy, otpSubmitting]);

  const handleResendOtp = async () => {
    if (!serverSignId || otpTimer > 0 || otpResendBusy) return;
    setOtpResendBusy(true);
    try {
      const result = await AuthService.registrationOtp(serverSignId, registeredBy);
      if (result?.success) {
        alertSuccessMessage(result?.message);
        setOtpTimer(60);
        setAttemptLeft(result?.attemptsLeft ?? "");
      } else {
        if (result?.message === "No OTP attempt left. Your account has been suspended.") {
          alertErrorMessage(result?.message);
          navigate(`/account-activate/${registrationToken}`);
        } else {
          alertErrorMessage(result?.message);
        }
      }
    } catch (e) {
      alertErrorMessage(e?.message || "Could not resend code.");
    } finally {
      setOtpResendBusy(false);
    }
  };

  const focusOtpIndex = (idx) => {
    requestAnimationFrame(() => {
      otpInputRefs.current[Math.min(Math.max(0, idx), 5)]?.focus();
    });
  };

  const getOtpDigitsStr = () => otpSingle.replace(/\D/g, "").slice(0, 6);

  const handleOtpCellChange = (index, e) => {
    if (otpDangerBorder) setOtpDangerBorder(false);
    const raw = e.target.value.replace(/\D/g, "");
    const d = getOtpDigitsStr();
    const before = d.slice(0, index);
    const after = d.slice(index + 1);
    if (!raw) {
      setOtpSingle(before + after);
      return;
    }
    const merged = (before + raw + after).replace(/\D/g, "").slice(0, 6);
    setOtpSingle(merged);
    focusOtpIndex(Math.min(index + raw.length, 5));
  };

  const handleOtpCellKeyDown = (index, e) => {
    if (otpDangerBorder) setOtpDangerBorder(false);
    const d = getOtpDigitsStr();
    if (e.key === "Backspace") {
      if (d[index]) {
        e.preventDefault();
        setOtpSingle(d.slice(0, index) + d.slice(index + 1));
      } else if (index > 0) {
        e.preventDefault();
        setOtpSingle(d.slice(0, index - 1) + d.slice(index));
        focusOtpIndex(index - 1);
      }
    }
  };

  const handleOtpRowPaste = (e) => {
    const text = e.clipboardData?.getData("text") || "";
    const raw = text.replace(/\D/g, "").slice(0, 6);
    if (!raw) return;
    e.preventDefault();
    setOtpDangerBorder(false);
    setOtpSingle(raw);
    focusOtpIndex(Math.min(raw.length, 5));
  };

  const handleOtpPaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      const raw = text.replace(/\D/g, "").slice(0, 6);
      setOtpDangerBorder(false);
      setOtpSingle(raw);
      focusOtpIndex(Math.min(raw.length, 5));
    } catch {
      setOtpDangerBorder(true);
      alertErrorMessage("Unable to read clipboard. Please paste manually (Ctrl+V) in the code boxes.");
    }
  }, []);

  const completeThirdPartySignup = async (result) => {
    if (result?.success) {
      alertSuccessMessage(result?.message);
      persistAuthTokenFromSignupResult(result);
      if (typeof handleUserDetails === "function" && localStorage.getItem("token")) {
        void handleUserDetails();
      }
      // New account: start KYC VARA onboarding (the user can skip it from there).
      navigate("/kyc-verification", { replace: true });
    } else {
      alertErrorMessage(result?.message);
    }
  };

  const handleSignupGoogle = async (tokenResponse) => {
    setGoogleSignupBusy(true);
    try {
      const result = await AuthService.signupwithGoogle(tokenResponse, "", invitation);
      await completeThirdPartySignup(result);
    } catch (error) {
      alertErrorMessage(error?.message);
    } finally {
      setGoogleSignupBusy(false);
    }
  };

  const SignupwithGoogle = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      if (tokenResponse.access_token) handleSignupGoogle(tokenResponse);
    },
  });

  const handleSignupApple = async () => {
    if (!getStatus("signup").enabled) {
      alertErrorMessage("Signup is temporarily off");
      return;
    }
    if (!agreeChecked) {
      setAgreeDangerBorder(true);
      alertErrorMessage("Please accept the Terms & Conditions to continue");
      return;
    }
    if (!isAppleSignInConfigured()) {
      alertErrorMessage("Apple sign-up is not configured.");
      return;
    }
    setGoogleSignupBusy(true);
    try {
      const signed = await signInWithApple();
      console.log("[Apple signup] signed data", signed);
      if (!signed?.idToken) return;
      const result = await AuthService.signupwithApple(signed, invitation);
      await completeThirdPartySignup(result);
    } catch (error) {
      alertErrorMessage(error?.message || "Apple sign-up failed");
    } finally {
      setGoogleSignupBusy(false);
    }
  };

  const maskedEmail = (email) => {
    if (!email || !email.includes("@")) return email;
    const [u, d] = email.split("@");
    if (u.length <= 2) return `${u[0]}***@${d}`;
    return `${u[0]}***${u.slice(-1)}@${d}`;
  };

  const maskedPhoneDisplay = () => {
    const raw = String(signId || "");
    if (raw.length <= 4) return `${countryCode} ${raw}`;
    return `${countryCode} ${raw.slice(0, 2)}***${raw.slice(-2)}`;
  };

  const goBackFromPasswordStep = () => {
    setWizardStep(1);
  };

  const goBackFromVerificationStep = () => {
    setWizardStep(2);
    setRegistrationToken("");
    setServerSignId("");
    setRegisteredBy("");
    setOtpSingle("");
    setOtpTimer(0);
    setAttemptLeft("");
  };

  const goBackFromWelcomeStep = () => {
    setSignupReachedWelcome(false);
    setWizardStep(3);
  };

  const otpDigitsDisplay = getOtpDigitsStr();

  const signupFormTitle =
    wizardStep === 1
      ? "Create Account"
      : wizardStep === 2
        ? "Set your password"
        : wizardStep === 3
          ? accountTab === "email"
            ? "Verify your email"
            : "Verify your phone"
          : wizardStep === 4
            ? ""
            : "Create Account";

  return (
    <>
      <Helmet>
        <title>Sign Up at AGCX – Start Crypto Trading Today</title>
        <meta
          name="description"
          content="Register for AGCX: create your account, verify email or phone, set password, and start trading."
        />
        <meta name="keywords" content="register crypto exchange, sign up AGCX, crypto account registration" />
      </Helmet>

      <div className="login_fullhieght">
        <div className="sign_in_form_s">
          <div className="sign_rewards">
            <h2>
              Create your account with <br /> <span style={{ color: "linear-gradient(135deg, #E8C97A, #C9A84C)" }}>Arab Global</span>  Crypto Exchange

            </h2>
            <div className="rewards_vector">
              <img className="rewards_vector" src="/images/new-images/rewards_vector.png" alt="rewards" />
            </div>
          </div>

          <div className="login_section bgsignup">
            <div className={`login_form_right${wizardStep === 4 ? " signup-wizard-welcome-wide" : ""}`}>
              <div className="form_block_login">
                <h2>{signupFormTitle}</h2>

                {wizardStep === 1 && (
                  <ul className="nav nav-tabs login-pills" id="signupTab" role="tablist">
                    <li className="nav-item" role="presentation">
                      <button
                        type="button"
                        className={`nav-link ${accountTab === "email" ? "active" : ""}`}
                        role="tab"
                        aria-selected={accountTab === "email"}
                        onClick={() => switchAccountTab("email")}
                      >
                        Email
                      </button>
                    </li>
                    <li className="nav-item" role="presentation">
                      <button
                        type="button"
                        className={`nav-link ${accountTab === "phone" ? "active" : ""}`}
                        role="tab"
                        aria-selected={accountTab === "phone"}
                        onClick={() => switchAccountTab("phone")}
                      >
                        Phone
                      </button>
                    </li>
                  </ul>
                )}

                <div className="tab-content" id="signupTabContent">
                  <div className="tab-pane fade show active" role="tabpanel">
                    {wizardStep === 1 && (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (step1Submitting) return;
                          void step1Next();
                        }}
                        noValidate
                      >
                        <div className="row">
                          {accountTab === "email" ? (
                            <div className="col-sm-12 input_block">
                              <EmailInputWithDomainSuggest
                                value={signId}
                                onChange={(e) => {
                                  if (step1EmailDangerBorder) setStep1EmailDangerBorder(false);
                                  setSignId(e.target.value);
                                }}
                                wrapClassName={`email_code eids-email-wrap${step1EmailDangerBorder ? " signup-step1-email--error" : ""}`}
                                placeholder="Enter email address"
                                suggestionsEnabled={accountTab === "email" && wizardStep === 1}
                              />
                            </div>
                          ) : (
                            <div className="col-sm-12 input_block">
                              <AuthPhoneField
                                inputId="signupCountryCode"
                                countryCode={countryCode}
                                onCountryChange={(code) => {
                                  if (step1PhoneDangerBorder) setStep1PhoneDangerBorder(false);
                                  setCountryCode(code);
                                }}
                                phone={signId}
                                onPhoneChange={(value) => {
                                  if (step1PhoneDangerBorder) setStep1PhoneDangerBorder(false);
                                  setSignId(value);
                                }}
                                phonePlaceholder="Enter phone number"
                                wrapClassName={step1PhoneDangerBorder ? "signup-step1-phone--error" : ""}
                              />
                            </div>
                          )}

                          <div className="col-sm-12 input_block">
                            <button
                              type="button"
                              className={`signup-wizard-referral-toggle ${referralOpen ? "is-open" : ""}`}
                              onClick={() => setReferralOpen((o) => !o)}
                              aria-expanded={referralOpen}
                            >
                              Referral code{" "}
                              {/* <span className="signup-wizard-referral-optional">(optional)</span>{" "} */}
                              <i className="ri-arrow-down-s-fill" />
                            </button>
                            {referralOpen && (
                              <div className={`auth-referral-field${referralDangerBorder ? " auth-referral-field--error" : ""}`}>
                                <input
                                  className="input_filed"
                                  type="text"
                                  placeholder="Referral code"
                                  value={invitation}
                                  onChange={(e) => {
                                    if (referralDangerBorder) setReferralDangerBorder(false);
                                    setInvitation(e.target.value);
                                  }}
                                  disabled={referralLocked}
                                />
                              </div>
                            )}
                          </div>

                          <div className="col-sm-12 input_block">
                            <div className={`signup-wizard-agree${agreeDangerBorder ? " signup-wizard-agree--error" : ""}`}>
                              <input
                                type="checkbox"
                                id="signup-user-agreement"
                                checked={agreeChecked}
                                onChange={(e) => {
                                  setAgreeChecked(e.target.checked);
                                  if (e.target.checked) setAgreeDangerBorder(false);
                                }}
                              />
                              <label htmlFor="signup-user-agreement">
                                I have read and agree to the{" "}
                                <button
                                  type="button"
                                  className="signup-wizard-agree__link"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setTermsOpen(true);
                                  }}
                                >
                                  Terms &amp; Conditions
                                </button>
                              </label>
                            </div>
                          </div>

                          <div className="col-sm-12 login_btn">
                            <button
                              className="next_btn signup-next-btn"
                              type="submit"
                              disabled={step1Submitting || !agreeChecked}
                            >
                              {step1Submitting ? (
                                <>
                                  <span className="signup-next-btn__spinner" aria-hidden />
                                  <span>Checking…</span>
                                </>
                              ) : (
                                "Next"
                              )}
                            </button>
                          </div>

                          <div className="col-sm-12">
                            <div className="signup-wizard-divider">Or log in with</div>
                            <div className="signup-wizard-social-row">
                              <button
                                type="button"
                                className="signup-wizard-social-btn"
                                disabled={googleSignupBusy}
                                onClick={() => {
                                  if (!getStatus("signup").enabled) {
                                    alertErrorMessage("Signup is temporarily off");
                                    return;
                                  }
                                  if (!agreeChecked) {
                                    setAgreeDangerBorder(true);
                                    alertErrorMessage("Please accept the Terms & Conditions to continue");
                                    return;
                                  }
                                  SignupwithGoogle();
                                }}
                                aria-label="Google"
                              >
                                {googleSignupBusy ? <span className="signup-social-btn__spinner" aria-hidden /> : <img src="/images/google_icon.svg" alt="" />}
                              </button>
                              <button
                                type="button"
                                className="signup-wizard-social-btn"
                                disabled={googleSignupBusy}
                                onClick={() => void handleSignupApple()}
                                aria-label="Apple"
                              >
                                {googleSignupBusy ? <span className="signup-social-btn__spinner" aria-hidden /> : <img src="/images/appleicon2.svg" alt="" />}
                              </button>
                            </div>
                          </div>

                          <div className="col-sm-12 registration__info agreetext">
                            {/* <p>
                              <Link to="/signup?entity=1">Signup as an entity</Link>
                            </p> */}
                            <p>Already have an account? <Link to="/login">Log In</Link>
                            </p>
                          </div>
                        </div>
                      </form>
                    )}

                    {wizardStep === 2 && (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (!passwordMeetsAllRules || registerSubmitting) return;
                          void submitPasswordAndRegister();
                        }}
                        noValidate
                      >
                        <div className="row">
                          <div className="col-sm-12 input_block">

                            <p className="signup-wizard-subtitle">Set the password to complete the signup</p>
                          </div>
                          <div className="col-sm-12 input_block">
                            <label htmlFor="signup-password-field">Password</label>
                            <div
                              className={`email_code signup-wizard-password-field${password.trim() ? "" : " signup-wizard-password-field--empty"}`}
                            >
                              <input
                                id="signup-password-field"
                                className="input_filed"
                                type={passwordInputType}
                                placeholder="Enter a password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="new-password"
                                style={{ paddingRight: password.trim() ? 44 : 14 }}
                              />
                              {password.trim().length > 0 && (
                                <div
                                  className="get_otp otp2 signup-wizard-password-toggle"
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => setShowPassword((s) => !s)}
                                  onKeyDown={(e) => e.key === "Enter" && setShowPassword((s) => !s)}
                                >
                                  {showPassword ? <i className="ri-eye-line" /> : <i className="ri-eye-off-line" />}
                                </div>
                              )}
                            </div>
                          </div>

                          <div
                            className={`col-sm-12 input_block signup-wizard-rule-list${password.trim() ? "" : " signup-wizard-rule-list--empty"}`}
                            aria-live="polite"
                          >
                            {signupPasswordRules.map((rule, idx) => {
                              const st = passwordRuleRowStates[idx];
                              const iconClass =
                                st === "ok"
                                  ? "ri-checkbox-circle-fill"
                                  : st === "bad"
                                    ? "ri-close-circle-fill"
                                    : st === "pending"
                                      ? "ri-indeterminate-circle-line"
                                      : "ri-checkbox-blank-circle-line";
                              return (
                                <div
                                  key={rule.id}
                                  className={`signup-wizard-rule-row signup-wizard-rule-row--${st}`}
                                >
                                  <i className={iconClass} aria-hidden />
                                  <span>{rule.label}</span>
                                </div>
                              );
                            })}
                          </div>

                          <div className="col-sm-12 login_btn">
                            <button
                              type="submit"
                              className={`signup-wizard-confirm-password${registerSubmitting ? " signup-wizard-confirm-password--busy" : ""}`}
                              disabled={!passwordMeetsAllRules || registerSubmitting}
                            >
                              {registerSubmitting ? (
                                <>
                                  <span className="signup-confirm-btn__spinner" aria-hidden />
                                  Creating…
                                </>
                              ) : (
                                "Confirm"
                              )}
                            </button>
                          </div>
                          {/* <div className="col-sm-12">
                            <button type="button" className="signup-wizard-back" onClick={goBackFromPasswordStep}>
                              ← Back
                            </button>
                          </div> */}
                        </div>
                      </form>
                    )}

                    {wizardStep === 3 && (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (verifyingTokenBusy || otpSubmitting || !serverSignId) return;
                          void handleOtpSubmit();
                        }}
                        noValidate
                      >
                        <div className="row">
                          <div className="col-sm-12 input_block">

                            <p className="signup-wizard-subtitle">
                              Enter the verification code sent to your {accountTab === "email" ? "email" : "phone"}{" "}
                              <strong>
                                {accountTab === "email"
                                  ? maskedEmail(
                                    (String(serverSignId || "").includes("@")
                                      ? String(serverSignId || "").trim()
                                      : String(signId || "").trim()) || ""
                                  )
                                  : maskedPhoneDisplay()}</strong>, valid for 10 minutes.
                            </p>
                          </div>

                          <div className="col-sm-12 input_block">
                            <label htmlFor="signup-otp-cell-0">Verification code</label>
                            <div
                              className={`signup-wizard-otp-stack${otpDangerBorder ? " signup-wizard-otp-stack--error" : ""}`}
                              onPaste={handleOtpRowPaste}
                              role="group"
                              aria-label="6-digit verification code"
                            >
                              <div className="signup-wizard-otp-row">
                                {[0, 1, 2, 3, 4, 5].map((i) => (
                                  <input
                                    key={i}
                                    id={i === 0 ? "signup-otp-cell-0" : undefined}
                                    ref={(el) => {
                                      otpInputRefs.current[i] = el;
                                    }}
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete={i === 0 ? "one-time-code" : "off"}
                                    name={i === 0 ? "one-time-code" : undefined}
                                    className="signup-wizard-otp-cell"
                                    maxLength={1}
                                    disabled={otpInputsLocked}
                                    value={otpDigitsDisplay[i] || ""}
                                    onChange={(e) => handleOtpCellChange(i, e)}
                                    onKeyDown={(e) => handleOtpCellKeyDown(i, e)}
                                    onPaste={handleOtpRowPaste}
                                    aria-label={`Digit ${i + 1} of 6`}
                                  />
                                ))}
                              </div>
                              <div className="signup-wizard-otp-actions">
                                <button
                                  type="button"
                                  className="signup-wizard-otp-link"
                                  disabled={otpTimer > 0 || otpResendBusy || verifyingTokenBusy || otpSubmitting}
                                  onClick={handleResendOtp}
                                >
                                  {otpResendBusy ? (
                                    <>
                                      <span className="signup-otp-link__spinner" aria-hidden />
                                      Sending…
                                    </>
                                  ) : otpTimer > 0 ? (
                                    `Resend (${otpTimer}s)`
                                  ) : (
                                    "Resend"
                                  )}
                                </button>
                                <button
                                  type="button"
                                  className="signup-wizard-otp-link signup-wizard-otp-paste"
                                  disabled={otpInputsLocked}
                                  onClick={handleOtpPaste}
                                >
                                  Paste
                                  <img src="/images/copyicon2.svg" alt="paste" />
                                </button>
                              </div>
                            </div>
                            {/* {attemptLeft !== "" && attemptLeft != null && (
                              <p className="signup-wizard-subtitle" style={{ marginTop: -8 }}>
                                Attempts left: {attemptLeft}
                              </p>
                            )} */}
                          </div>

                          <div className="col-sm-12 login_btn">
                            <button
                              className={`next_btn${verifyingTokenBusy || otpSubmitting ? " signup-next-btn" : ""}`}
                              type="submit"
                              disabled={verifyingTokenBusy || otpSubmitting || !serverSignId}
                            >
                              {verifyingTokenBusy ? (
                                <>
                                  <span className="signup-next-btn__spinner" aria-hidden />
                                  Setting up…
                                </>
                              ) : otpSubmitting ? (
                                <>
                                  <span className="signup-next-btn__spinner" aria-hidden />
                                  Verifying…
                                </>
                              ) : (
                                "Next"
                              )}
                            </button>
                          </div>
                          <div className="col-sm-12 signup-wizard-otp-actions" style={{ justifyContent: "flex-start", marginTop: 4 }}>
                            {/* <button type="button" className="signup-wizard-back" style={{ margin: 0 }} onClick={goBackFromVerificationStep}>
                              ← Back
                            </button> */}
                            {/* <button
                              type="button"
                              className="signup-wizard-back"
                              style={{ margin: 0 }}
                              onClick={handleResendOtp}
                              disabled={otpTimer > 0 || otpResendBusy || verifyingTokenBusy || otpSubmitting}
                            >
                              Didn&apos;t receive the code?
                            </button> */}
                          </div>
                        </div>
                      </form>
                    )}

                    {wizardStep === 4 && (
                      <div className="row">
                        <div className="col-sm-12 input_block text-center">
                          <div className="signup-wizard-welcome-img">
                            <img src="/images/login_sucessfull_img.svg" alt="" />
                          </div>
                          <h3 className="mb-2 verify_hd">
                            Welcome to AGCX Verify your identity to claim <span>exciting bonus</span>.
                          </h3>
                          {/* <p className="signup-wizard-subtitle" style={{ textAlign: "center" }}>
                            Verify your identity to claim <span style={{ color: "#d1aa67" }}>exciting bonus</span>.
                          </p> */}
                        </div>
                        <div className="col-sm-12 input_block">
                          <table className="signup-wizard-priv-table">
                            <thead>
                              <tr>
                                <th>Privileges</th>
                                <th>Not Verified</th>
                                <th>Verified</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>Withdrawal</td>
                                <td><img src="/images/dassed_icon.svg" alt="check" /></td>
                                <td>
                                  <strong>3M USDT</strong>
                                </td>
                              </tr>
                              <tr>
                                <td>Deposit</td>
                                <td><img src="/images/dassed_icon.svg" alt="check" /></td>
                                <td>
                                  <img src="/images/checkarrow.svg" alt="check" />
                                </td>
                              </tr>
                              <tr>
                                <td>Trading</td>
                                <td><img src="/images/dassed_icon.svg" alt="check" /></td>
                                <td>
                                  <img src="/images/checkarrow.svg" alt="check" />
                                </td>
                              </tr>
                              <tr>
                                <td>P2P</td>
                                <td><img src="/images/dassed_icon.svg" alt="check" /></td>
                                <td>
                                  <img src="/images/checkarrow.svg" alt="check" />
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="col-sm-12 login_btn">
                          <button
                            type="button"
                            className="next_btn"
                            onClick={() => navigate("/kyc-verification", { replace: true })}
                          >
                            Verify Now
                          </button>
                        </div>
                        <div className="col-sm-12">
                          <div className="signup-wizard-secure">
                            <img src="/images/secure_icon.svg" alt="secure" />
                            Your information is securely encrypted on AGCX.
                          </div>
                        </div>
                        {/* <div className="col-sm-12">
                          <button type="button" className="signup-wizard-back" onClick={goBackFromWelcomeStep}>
                            ← Back
                          </button>
                        </div> */}

                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade scaner_pop_up thankyou_s" id="Confirmation_model" tabIndex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <div className="thanky_top_hd">
                <img src="/images/certified_vector.svg" alt="thankyou" />
                <h2>Thank you for signing up with us!</h2>
              </div>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close">
                x
              </button>
            </div>
            <div className="modal-body">
              <p>To complete your registration and activate your account, please verify your email address by clicking the link we’ve sent to your inbox.</p>
              <p>✅ Check your email and click on the verification link to proceed.</p>
              <p>If you didn’t receive the email, please check your spam or junk folder. You can also request a new verification link if needed.</p>
              <p>Thank you for being a part of our community!</p>
            </div>
            <div className="okbtn">
              <button className="save_btn" type="button" data-bs-dismiss="modal">
                Ok
              </button>
            </div>
          </div>
        </div>
      </div>

      <TermsConditionsModal
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        onAccept={() => {
          setAgreeChecked(true);
          setAgreeDangerBorder(false);
          setTermsOpen(false);
        }}
      />
    </>
  );
};

export default SignupPage;
